import axios from 'axios';
import { getIdToken } from './Authservice';

// Base URL for API endpoints - would be configured per environment
//const API_BASE_URL = 'https://ccadevarchivaluiapiaks.molinahealthcare.com/api';
const API_BASE_URL =process.env.REACT_APP_API_BASE_URL ;

// Extract URL parameters from current URL
const extractUrlParams = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const params = {};

  // Extract specific parameters we're interested in
  params.sessionId = searchParams.get('SessionId'); // Match the case in backend
  // params.UserID = searchParams.get('UserID');
  // params.MemberID = searchParams.get('MemberID');
  params.patientId = searchParams.get('PatientId'); // Match the case in backend
  // params.HRAId = searchParams.get('HRAId');
  
  
  return params;
};

// Create axios instance with common configuration
const createApiClient = async () => {
  // Get the ID token for authentication
  const idToken = await getIdToken();
  
  return axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`, // Add the Bearer token to all requests
      'accept': '*/*'
    }
  });
};

// Helper function to build query parameters for API calls
const buildQueryParams = (params) => {
  const queryParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined) {
      queryParams.append(key, value);
    }
  }
  return queryParams.toString();
};

// Progress notes service methods
const progressNotesService = {
  // Get progress notes
  getProgressNotes: async () => {
    try {
      // Dynamically extract query params from URL
      const { sessionId, patientId } = extractUrlParams();
      // Optionally extract AuthType if present
      const authType = new URLSearchParams(window.location.search).get('AuthType');
      if (!sessionId || !patientId) {
        throw new Error('Required session or patient data is missing');
      }

      const queryParams = buildQueryParams({
        sessionId: sessionId,  // Encrypted sessionId from URL
        patientId: patientId  // Encrypted patientId from URL
      });

      const apiClient = await createApiClient();
      const response = await apiClient.get(`/ProgressNotes/GetProgressNotes?${queryParams}`);

      if (response.status === 200 && response.data) {
        // Extract decrypted data from the response
        const decryptedData = {
          sessionId: response.data.sessionId || response.data.SessionId,
          patientId: response.data.patientId || response.data.PatientId,
          chunkSize: response.data.chunkSize || response.data.ChunkSize,
          isDownloaded: response.data.isDownloaded || response.data.IsDownloaded,
        };

        // Save both the original response and decrypted parameters
        sessionStorage.setItem('progressNotesDecryptedParams', JSON.stringify(decryptedData));
        sessionStorage.setItem('progressNotesFullData', JSON.stringify(response.data));

        // Store encrypted parameters for navigation and other modules
        const encryptedParams = {
          SessionId: sessionId,
          PatientId: patientId,
          AuthType: authType || ''
        };
        sessionStorage.setItem('progressNotesEncryptedParams', JSON.stringify(encryptedParams));
      } else {
        throw new Error(`Failed to fetch progress notes: ${response.status}`);
      }

      return response;
    } catch (error) {
      console.error('Error in getProgressNotes:', error);
      throw error;
    }
  },

  // Load Progress Notes Master using decrypted sessionid and patientId from getProgressNotes response
  loadProgressNotesMaster: async () => {
    try {
      // Get the decrypted parameters from the stored response
      const decryptedParams = sessionStorage.getItem('progressNotesDecryptedParams');
      if (!decryptedParams) {
        throw new Error('Decrypted parameters not found. Please fetch progress notes first.');
      }

      const params = JSON.parse(decryptedParams);
      // Use the decrypted sessionId from the response
      const sessionId = params.sessionId || params.SessionId;
      const patientId = params.patientId || params.PatientId;

      if (!sessionId || !patientId) {
        throw new Error('Required decrypted session or patient data is missing');
      }

      // Build query with decrypted parameters
      const queryParams = buildQueryParams({
        sessionId: sessionId,  // Use decrypted sessionId
        patientId: patientId  // Use decrypted patientId
      });

      const apiClient = await createApiClient();
      const response = await apiClient.get(`/ProgressNotes/LoadProgressNotes?${queryParams}`);

      if (response.status !== 200) {
        throw new Error(`Failed to load progress notes master: ${response.status}`);
      }
      if (response.data && response.data.message === 'Session Expired.') {
        const sessionExpiredError = new Error('Session Expired.');
        sessionExpiredError.isSessionExpired = true;
        throw sessionExpiredError;
      }

      // Log the response for debugging
      console.log('LoadProgressNotes API Response:', response.data);

      // Store the master data
      if (response.data) {
        // If the data is an array, store it directly, otherwise wrap it in an array
        let dataToStore = Array.isArray(response.data) ? response.data : [response.data];
        // Normalize file_Name property for each record
        dataToStore = dataToStore.map(item => {
          // Some APIs may return file_Name, FileName, or filename
          item.file_Name = item.file_Name || item.FileName || item.filename || '';
          return item;
        });
        
        try {
          sessionStorage.setItem('progressNotesMasterData', JSON.stringify(dataToStore));
        } catch (error) {
          if (error.name === 'QuotaExceededError') {
            console.warn('SessionStorage quota exceeded. Attempting to clear old data and retry...');
            
            // Clear other progress notes data to free up space
            ['progressNotesFullData', 'selectedProgressNoteData', 'progressNotesFullTextViewData'].forEach(key => {
              sessionStorage.removeItem(key);
            });
            
            try {
              // Try storing again after cleanup
              sessionStorage.setItem('progressNotesMasterData', JSON.stringify(dataToStore));
            } catch (retryError) {
              console.error('Still unable to store data after cleanup. Data is too large for sessionStorage.');
              
              // Fallback: Store only essential data
              const essentialData = dataToStore.map(item => ({
                id: item.id,
                final: item.final,
                source: item.source,
                case_Type_Id_Verbose: item.case_Type_Id_Verbose,
                registrar: item.registrar,
                subject: item.subject,
                security_Level: item.security_Level,
                tzix_Id: item.tzix_Id || item.case_Id,
                case_Id: item.case_Id,
                case_Name: item.case_Name,
                create_Date: item.create_Date,
                void_reason: item.void_reason,
                file_Name: item.file_Name
              }));
              
              try {
                sessionStorage.setItem('progressNotesMasterData', JSON.stringify(essentialData));
                console.log('Stored reduced dataset successfully');
              } catch (finalError) {
                console.error('Dataset too large for sessionStorage. Consider implementing server-side pagination.');
                throw new Error('Dataset too large for sessionStorage. Please contact support.');
              }
            }
          } else {
            console.error('Error storing data in sessionStorage:', error);
            throw error;
          }
        }
      }

      return response;
    } catch (error) {
      console.error('Error in loadProgressNotesMaster:', error);
      throw error;
    }
  },


  // Check if file exists and download if present
  checkFileExists: async (fileName, patientId, notepadId) => {
    try {
      const decryptedParams = sessionStorage.getItem('progressNotesDecryptedParams');
      let resolvedPatientId = patientId;

      if (!resolvedPatientId && decryptedParams) {
        const params = JSON.parse(decryptedParams);
        resolvedPatientId = params.patientId || params.PatientId || '';
      }

      if (!resolvedPatientId || !notepadId) {
        throw new Error('Required patientId or notepadId is missing for file download.');
      }

      const apiClient = await createApiClient();
      const payload = { 
        fileName, 
        patientId: String(resolvedPatientId),
        notepadId: String(notepadId)
      };
      const response = await apiClient.post('/ProgressNotes/DownloadFile/downloadfile', payload);
      // API returns 'true' or 'false' as text
      if (response.data === true || response.data === 'true') {
        // Download file if exists
        const downloadResponse = await apiClient.post('/ProgressNotes/FileDownload/filedownload', payload, {
          responseType: 'blob',
          headers: {
            'Accept': 'application/pdf'
          }
        });
        if (downloadResponse.status !== 200) {
          throw new Error('Failed to download file');
        }
        // Create a blob from the PDF data
        const blob = new Blob([downloadResponse.data], { type: 'application/pdf' });
        // Create a link element and trigger download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName || 'ProgressNoteAttachment.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error checking/downloading file:', error);
      throw error;
    }
  },
  
  // Get selected progress note details
  getSelectedRowDetails: async (notepadId) => {
    try {
      const decryptedParams = sessionStorage.getItem('progressNotesDecryptedParams');
      if (!decryptedParams) {
        throw new Error('Decrypted parameters not found. Please fetch progress notes first.');
      }

      const params = JSON.parse(decryptedParams);
      const sessionId = params.sessionId || params.SessionId;
      const patientId = params.patientId || params.PatientId;
      const isDownloaded = params.isDownloaded || params.IsDownloaded;
      const chunkSize = params.chunkSize || params.ChunkSize;

      if (!sessionId || !patientId) {
        throw new Error('Required decrypted session or patient data is missing');
      }

      const queryParams = buildQueryParams({
        sessionId,
        patientId,
        notepadId,
        isDownloaded,
        chunkSize
      });

      const apiClient = await createApiClient();
      const response = await apiClient.get(`/ProgressNotes/ProgressNotesSelectedRow?${queryParams}`);

      if (response.status !== 200) {
        throw new Error(`Failed to fetch selected row details: ${response.status}`);
      }
      if(response.data){
        sessionStorage.setItem('selectedProgressNoteData', JSON.stringify(response.data));
      }

      return response.data;
    } 
    
    
    catch (error) {
      console.error('Error in getSelectedRowDetails:', error);
      throw error;
    }
  },

  // Get assessment list using the stored decrypted parameters
  
  // Get assessment details by ID

  // Export selected progress note to PDF
  exportSelectedToPDF: async (notepadId, htmlContent) => {
    try {
      const decryptedParams = sessionStorage.getItem('progressNotesDecryptedParams');
      if (!decryptedParams) {
        throw new Error('Decrypted parameters not found. Please fetch progress notes first.');
      }

      const params = JSON.parse(decryptedParams);
      const sessionId = params.sessionId || params.SessionId;
      const patientId = params.patientId || params.PatientId;
      const isDownloaded = params.isDownloaded || params.IsDownloaded;
      const chunkSize = params.chunkSize || params.ChunkSize;
      
      if (!sessionId || !patientId) {
        throw new Error('Required decrypted session or patient data is missing');
      }

      // Prepare payload for POST request
    
     const payload = {
 
    sessionId,
    patientId,
    notepadId: String(notepadId),
    isDownloaded,
    chunkSize,
    htmlContent
  }

  

      const apiClient = await createApiClient();
      // First API call to get file details (POST with JSON body)
      const exportResponse = await apiClient.post(`/ProgressNotes/ExportSelectedPDF`, payload);

      if (exportResponse.status !== 200 || !exportResponse.data) {
        throw new Error('Failed to get PDF export details');
      }

      if (!exportResponse.data || !exportResponse.data.fileGuid || !exportResponse.data.fileName) {
        throw new Error('File details not found in response');
      }

      // Second API call to download the file
      const downloadParams = {
        fileGuid: exportResponse.data.fileGuid,
        fileName: exportResponse.data.fileName
      };

      const downloadResponse = await apiClient.post(`/ProgressNotes/ProgressNoteDownload`, downloadParams, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf'
        }
      });

      if (downloadResponse.status !== 200) {
        throw new Error(`Failed to download PDF: ${downloadResponse.status}`);
      }

      // Create a blob from the PDF data
      const blob = new Blob([downloadResponse.data], { type: 'application/pdf' });

      // Create a link element and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = exportResponse.data.fileName || 'ProgressNotes.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return downloadResponse;
    } catch (error) {
      console.error('Error in exportSelectedToPDF:', error);
      throw error;
    }
  },

  // Export all progress notes to PDF
  // exportToPDF: async (offsetSize, isDownloaded) => {
  //   try {
  //     const decryptedParams = sessionStorage.getItem('progressNotesDecryptedParams');
  //     if (!decryptedParams) {
  //       throw new Error('Decrypted parameters not found. Please fetch progress notes first.');
  //     }

  //     const params = JSON.parse(decryptedParams);
  //     const sessionId = params.sessionId || params.SessionId;
  //     const patientId = params.patientId || params.PatientId;

  //     if (!sessionId || !patientId) {
  //       throw new Error('Required decrypted session or patient data is missing');
  //     }

  //     const queryParams = buildQueryParams({
  //       sessionId,
  //       patientId,
  //       offsetSize,
  //       isDownloaded
  //     });

  //     const apiClient = await createApiClient();
  //     const response = await apiClient.get(`/ProgressNotes/ExportPDF/exportpdf?${queryParams}`, {
  //       responseType: 'blob' // Important: tells axios to expect binary data
  //     });
  //     //  const downloadParams = buildQueryParams({
  //     //   fileGuid: response.data.fileGuid,
  //     //   filename: response.data.fileName
  //     // });

  //     // const downloadResponse = await apiClient.get(`/ProgressNotes/ProgressNoteDownload?${downloadParams}`, {
  //     //   responseType: 'blob',
  //     //   headers: {
  //     //     'Accept': 'application/pdf'
  //     //   }
  //     // });

  //     if (downloadResponse.status !== 200) {
  //       throw new Error(`Failed to download PDF: ${downloadResponse.status}`);
  //     }

  //     // Create a blob from the PDF data
  //     const blob = new Blob([downloadResponse.data], { type: 'application/pdf' });

  //     // Create a link element and trigger download
  //     const url = window.URL.createObjectURL(blob);
  //     const link = document.createElement('a');
  //     link.href = url;
  //     link.download = 'ProgressNotes.pdf';
  //     document.body.appendChild(link);
  //     link.click();
  //     document.body.removeChild(link);
  //     window.URL.revokeObjectURL(url);

  //     return response;
  //   } catch (error) {
  //     console.error('Error in exportToPDF:', error);
  //     throw error;
  //   }
  // },

  exportToPDF: async (offsetSize, isDownloaded, htmlContent) => {
    try {
      const decryptedParams = sessionStorage.getItem('progressNotesDecryptedParams');
      if (!decryptedParams) {
        throw new Error('Decrypted parameters not found. Please fetch progress notes first.');
      }

      const params = JSON.parse(decryptedParams);
      const sessionId = params.sessionId || params.SessionId;
      const patientId = params.patientId || params.PatientId;

      if (!sessionId || !patientId) {
        throw new Error('Required decrypted session or patient data is missing');
      }

      // Prepare payload for POST request
      const payload = {
        sessionId,
        patientId,
        offsetSize,
        isDownloaded,
        htmlContent
      };

      const apiClient = await createApiClient();
      // First API call to get file details (POST with JSON body)
      const exportResponse = await apiClient.post(`/ProgressNotes/ExportPDF/exportpdf`, payload);

      if (exportResponse.status !== 200 || !exportResponse.data) {
        throw new Error('Failed to get PDF export details');
      }

      if (!exportResponse.data || !exportResponse.data.fileGuid || !exportResponse.data.fileName) {
        throw new Error('File details not found in response');
      }

      // Second API call to download the file
      const downloadParams = {
        fileGuid: exportResponse.data.fileGuid,
        fileName: exportResponse.data.fileName
      };

      const downloadResponse = await apiClient.post(`/ProgressNotes/ProgressNoteDownload`, downloadParams, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf'
        }
      });

      if (downloadResponse.status !== 200) {
        throw new Error(`Failed to download PDF: ${downloadResponse.status}`);
      }

      // Create a blob from the PDF data
      const blob = new Blob([downloadResponse.data], { type: 'application/pdf' });

      // Create a link element and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = exportResponse.data.fileName || 'ProgressNotes.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return downloadResponse;
    } catch (error) {
      console.error('Error in exportToPDF:', error);
      throw error;
    }
  },

  // Get Full Text View
  getFullTextView: async (offsetSize , isDownloaded) => {
    try {
      // Get the decrypted parameters from stored data
      const decryptedParams = sessionStorage.getItem('progressNotesDecryptedParams');
      if (!decryptedParams) {
        throw new Error('Decrypted parameters not found. Please fetch progress notes first.');
      }

      const params = JSON.parse(decryptedParams);
      const sessionId = params.sessionId || params.SessionId;
      const patientId = params.patientId || params.PatientId;

      if (!sessionId || !patientId) {
        throw new Error('Required decrypted session or patient data is missing');
      }

      const queryParams = buildQueryParams({
        sessionId,
        patientId,
        offsetSize,
        isDownloaded
      });

      const apiClient = await createApiClient();
      const response = await apiClient.get(`/ProgressNotes/ProgressNotesFullTextView?${queryParams}`);

      if (response.status !== 200) {
        throw new Error(`Failed to fetch full text view: ${response.status}`);
      }
      
      if(response.data){
        try {
          sessionStorage.setItem('progressNotesFullTextViewData', JSON.stringify(response.data));
        } catch (error) {
          if (error.name === 'QuotaExceededError') {
            console.warn('SessionStorage quota exceeded for FullTextView data. Attempting to clear old data and retry...');
            
            // Clear other progress notes data to free up space
            ['progressNotesMasterData', 'selectedProgressNoteData', 'progressNotesFullData'].forEach(key => {
              sessionStorage.removeItem(key);
            });
            
            try {
              // Try storing again after cleanup
              sessionStorage.setItem('progressNotesFullTextViewData', JSON.stringify(response.data));
            } catch (retryError) {
              console.error('Still unable to store FullTextView data after cleanup. Data is too large for sessionStorage.');
              
              // Fallback: Store only essential data for FullTextView
              const essentialData = Array.isArray(response.data) ? response.data.map(item => ({
                id: item.id,
                subject: item.subject,
                create_Date: item.create_Date,
                content: item.content ? item.content.substring(0, 1000) + '...' : '', // Truncate content
                source: item.source,
                registrar: item.registrar,
                case_Name: item.case_Name,
                tzix_Id: item.tzix_Id || item.case_Id,
                security_Level: item.security_Level,
                void_reason: item.void_reason
              })) : [];
              
              try {
                sessionStorage.setItem('progressNotesFullTextViewData', JSON.stringify(essentialData));
                console.log('Stored reduced FullTextView dataset successfully');
              } catch (finalError) {
                console.error('FullTextView dataset too large for sessionStorage. Consider implementing server-side pagination.');
                // Don't throw error here, continue with data in memory only
                console.warn('Continuing without sessionStorage for FullTextView data');
              }
            }
          } else {
            console.error('Error storing FullTextView data in sessionStorage:', error);
            // Don't throw error here, data can still be used from response
            console.warn('Continuing without sessionStorage for FullTextView data');
          }
        }
      }

      return response.data;
    } catch (error) {
      console.error('Error in getFullTextView:', error);
      throw error;
    }
  }
};

export default progressNotesService;
